"use client";

import React, { useState, useEffect, useRef } from "react";
import { Check, Zap, LayoutDashboard, Smartphone, Search, Mail, Phone, RefreshCw, Headphones, PenTool, Video, Camera, ArrowRight, Monitor, Globe, ShieldCheck, MousePointerClick } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface FeatureItem {
  title: string;
  description: string;
  icon: React.ElementType;
}
interface PackageSection {
  id: string;
  title: string;
  price: string;
  description: string;
  features: FeatureItem[];
  cta: string;
  ctaLink: string;
}
const packages: PackageSection[] = [{
  id: "starter",
  title: "Starter Package",
  price: "$995 + GST",
  description: "Perfect for small businesses needing a professional online presence.",
  cta: "View Starter Details",
  ctaLink: "/StarterPackagePage",
  features: [{
    title: "Custom Modern Design",
    description: "Unique design tailored to your brand, not a generic template.",
    icon: LayoutDashboard
  }, {
    title: "Mobile Responsive",
    description: "Looks perfect on all devices: phones, tablets, and desktops.",
    icon: Smartphone
  }, {
    title: "3 Page Setup",
    description: "Home, Services, and Contact pages professionally built.",
    icon: Monitor
  }, {
    title: "Basic SEO",
    description: "Optimized structure to help customers find you on Google.",
    icon: Search
  }, {
    title: "Contact Integration",
    description: "Custom contact forms and click-to-call functionality.",
    icon: Mail
  }, {
    title: "Hosting Included",
    description: "12 months of secure, fast hosting and domain registration.",
    icon: Globe
  }]
}, {
  id: "advanced",
  title: "Advanced Package",
  price: "$1400 + GST",
  description: "For growing businesses requiring more content and branding support.",
  cta: "View Advanced Details",
  ctaLink: "/AdvancedPackagePage",
  features: [{
    title: "Expanded 5 Page Site",
    description: "More space to showcase your services and portfolio.",
    icon: LayoutDashboard
  }, {
    title: "Branding Session",
    description: "1-hour professional consultation to refine your visual identity.",
    icon: PenTool
  }, {
    title: "Enhanced Updates",
    description: "5 content changes per month to keep your site fresh.",
    icon: RefreshCw
  }, {
    title: "Unlimited Tech Support",
    description: "Direct access to our team for any technical questions.",
    icon: Headphones
  }, {
    title: "Priority SEO Setup",
    description: "Advanced meta tagging and content structure optimization.",
    icon: Search
  }, {
    title: "Performance Tuned",
    description: "Optimized for lightning-fast load times and conversion.",
    icon: Zap
  }]
}];
export default function FeatureShowcase() {
  const [activePackage, setActivePackage] = useState<string>("starter");
  const starterRef = useRef<HTMLDivElement>(null);
  const advancedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!starterRef.current || !advancedRef.current) return;
      const starterRect = starterRef.current.getBoundingClientRect();
      const advancedRect = advancedRef.current.getBoundingClientRect();
      const triggerPoint = window.innerHeight * 0.4;
      if (advancedRect.top < triggerPoint + 200) {
        setActivePackage("advanced");
      } else {
        setActivePackage("starter");
      }
    };
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="featureshowcase" className="bg-base min-h-screen relative overflow-hidden font-sans">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          
          {}
          <div className="hidden lg:flex flex-col justify-center sticky top-0 h-screen py-20 z-10">
            <div className="relative w-full max-w-xl mx-auto">
              
              {}
              <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] rounded-full blur-[120px] opacity-20 transition-colors duration-1000 ${activePackage === 'starter' ? 'bg-primary' : 'bg-purple-500'}`}></div>
              
              {}
              <div className="relative z-10 space-y-8">
                <div className="space-y-4 transition-all duration-500">
                  <Badge variant="outline" className={`px-4 py-1.5 text-sm border-opacity-30 backdrop-blur-md ${activePackage === 'starter' ? 'text-primary border-primary bg-primary/10' : 'text-purple-400 border-purple-500 bg-purple-500/10'}`}>
                    {activePackage === 'starter' ? 'Entry Level' : 'Growth Focused'}
                  </Badge>
                  
                  <h2 className="text-4xl md:text-5xl font-extrabold text-base tracking-tight leading-[1.1]">
                    {activePackage === 'starter' ? 'Essential Tools for' : 'Scale Your Business'}
                    <span className={`block mt-2 ${activePackage === 'starter' ? 'text-primary' : 'text-purple-400'} transition-colors duration-500`}>
                      {activePackage === 'starter' ? 'Small Business.' : 'With Pro Features.'}
                    </span>
                  </h2>
                  
                  <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                    {activePackage === 'starter' ? "Everything you need to get online quickly, professionally, and affordably. No hidden fees, just results." : "Advanced tools, more pages, and dedicated branding support to help you stand out in a crowded market."}
                  </p>
                </div>

                {}
                <div className="relative aspect-video rounded-2xl overflow-hidden border border-border shadow-2xl bg-card group transition-all duration-500 hover:border-primary/20">
                  {}
                  <div className="absolute inset-0 bg-gradient-to-br from-foreground/5 to-transparent opacity-50 pointer-events-none"></div>
                  
                  {}
                  <div className="absolute inset-0 flex items-center justify-center p-8">
                    {activePackage === 'starter' ? <div className="grid grid-cols-2 gap-6 w-full h-full animate-in fade-in zoom-in duration-700">
                        <MockupItem data-index={0} icon={Monitor} label="Responsive" delay={0} color="text-primary" />
                        <MockupItem data-index={1} icon={Zap} label="Fast Loading" delay={150} color="text-primary" />
                        <MockupItem data-index={2} icon={Search} label="SEO Ready" delay={300} color="text-primary" />
                        <MockupItem data-index={3} icon={Phone} label="Click-to-Call" delay={450} color="text-primary" />
                      </div> : <div className="grid grid-cols-2 gap-6 w-full h-full animate-in fade-in zoom-in duration-700">
                        <MockupItem data-index={0} icon={PenTool} label="Branding" delay={0} color="text-purple-400" />
                        <MockupItem data-index={1} icon={LayoutDashboard} label="5 Pages" delay={150} color="text-purple-400" />
                        <MockupItem data-index={2} icon={Headphones} label="Priority Support" delay={300} color="text-purple-400" />
                        <MockupItem data-index={3} icon={RefreshCw} label="Monthly Updates" delay={450} color="text-purple-400" />
                      </div>}
                  </div>
                </div>

                {}
                <div className="flex flex-wrap gap-4 pt-4">
                   <Button asChild size="lg" className={`h-12 px-8 rounded-full font-bold text-base transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 ${activePackage === 'starter' ? "bg-primary hover:bg-primary/90 text-primary-foreground" : "bg-purple-600 hover:bg-purple-500 text-white"}`}>
                      <Link to={activePackage === 'starter' ? "/StarterPackagePage" : "/AdvancedPackagePage"} className="inline-flex items-center justify-center gap-2">
                        {activePackage === 'starter' ? "Get Started Now" : "Upgrade to Advanced"}
                      </Link>
                   </Button>
                   <Button asChild variant="outline" size="lg" className="h-12 px-8 rounded-full border-border text-base hover:bg-muted">
                      <Link to="/ContactPage">Contact Sales</Link>
                   </Button>
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="py-12 lg:py-24 relative">
            
            {}
            <div className="lg:hidden mb-12 space-y-4">
              <Badge variant="outline" className="text-primary border-primary/30 bg-primary/10">Features</Badge>
              <h2 className="text-3xl font-extrabold text-base">Our Packages</h2>
              <p className="text-muted-foreground">Scroll to explore what's included in our website packages.</p>
            </div>

            {}
            <div className="absolute left-6 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-primary/30 to-transparent hidden md:block lg:left-8"></div>

            <div className="space-y-32">
              
              {}
              <div ref={starterRef} id="package-starter" className="relative scroll-mt-24">
                <SectionHeader title="Starter Package" price="$995 + GST" description="Perfect for small businesses needing a professional online presence." color="text-primary" markerColor="bg-primary" />
                
                <div className="grid grid-cols-1 gap-6 pl-0 md:pl-16 lg:pl-20">
                  {packages[0].features.map((feature, index) => <FeatureCard key={index} feature={feature} index={index} colorClass="text-primary" bgHoverClass="group-hover:border-primary/30" />)}
                </div>

                <div className="mt-8 pl-0 md:pl-16 lg:pl-20 lg:hidden">
                  <Button asChild className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-full h-12">
                    <Link to="/StarterPackagePage">View Starter Details</Link>
                  </Button>
                </div>
              </div>

              {}
              <div ref={advancedRef} id="package-advanced" className="relative scroll-mt-24">
                <SectionHeader title="Advanced Package" price="$1400 + GST" description="For growing businesses requiring more content and branding support." color="text-purple-400" markerColor="bg-purple-400" />

                <div className="grid grid-cols-1 gap-6 pl-0 md:pl-16 lg:pl-20">
                  {packages[1].features.map((feature, index) => <FeatureCard key={index} feature={feature} index={index} colorClass="text-purple-400" bgHoverClass="group-hover:border-purple-400/30" />)}
                </div>

                <div className="mt-8 pl-0 md:pl-16 lg:pl-20 hidden lg:block">
                  <Button asChild className="bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-full h-12 px-10 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                    <Link to="/AdvancedPackagePage">Learn More</Link>
                  </Button>
                </div>

                <div className="mt-8 pl-0 md:pl-16 lg:pl-20 lg:hidden">
                  <Button asChild className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold rounded-full h-12">
                    <Link to="/AdvancedPackagePage">View Advanced Details</Link>
                  </Button>
                </div>
              </div>

            </div>

            
          </div>

        </div>
      </div>
    </section>;
}
function MockupItem({
  icon: Icon,
  label,
  delay,
  color,
  "data-index": dataIndex
}: {
  icon: React.ElementType;
  label: string;
  delay: number;
  color: string;
  "data-index"?: number;
}) {
  return <div data-index={dataIndex} className="bg-base/80 backdrop-blur-sm rounded-xl p-4 border border-border/50 flex flex-col items-center justify-center gap-3 shadow-lg" style={{
    animation: `pulse 3s infinite ${delay}ms`
  }}>
      <div className={`p-3 rounded-full bg-foreground/5 ${color}`}>
        <Icon className="w-8 h-8" />
      </div>
      <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{label}</span>
    </div>;
}
function SectionHeader({
  title,
  price,
  description,
  color,
  markerColor
}: {
  title: string;
  price: string;
  description: string;
  color: string;
  markerColor: string;
}) {
  return <div className="mb-10 pl-0 md:pl-16 lg:pl-20 relative">
      {}
      <div className={`absolute left-6 lg:left-8 top-3 w-4 h-4 rounded-full border-4 border-base ${markerColor} -translate-x-1/2 hidden md:block z-10 shadow-lg`}></div>
      
      <h3 className={`text-xl font-bold mb-2 uppercase tracking-wide ${color}`}>{title}</h3>
      <div className="text-4xl md:text-5xl font-extrabold text-base mb-4 tracking-tight">{price}</div>
      <p className="text-muted-foreground text-lg leading-relaxed max-w-lg">{description}</p>
    </div>;
}
function FeatureCard({
  feature,
  index,
  colorClass,
  bgHoverClass
}: {
  feature: FeatureItem;
  index: number;
  colorClass: string;
  bgHoverClass: string;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const Icon = feature.icon;
  return <div ref={ref} data-index={index} className={`transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`} style={{
    transitionDelay: `${index * 50}ms`
  }}>
      <Card className={`bg-card border-border transition-all duration-300 overflow-hidden group hover:bg-muted/50 ${bgHoverClass}`}>
        <CardContent className="p-6 flex items-start gap-5">
          <div className={`mt-1 p-3 rounded-xl bg-base border border-border shadow-lg shrink-0 group-hover:scale-110 transition-transform duration-300 ${colorClass}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="space-y-2">
            <h4 className="text-lg font-bold text-base group-hover:text-base transition-colors duration-300 font-sans">
              {feature.title}
            </h4>
            <p className="text-sm text-muted-foreground leading-relaxed group-hover:text-foreground transition-colors">
              {feature.description}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>;
}